import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Bot, RefreshCw, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const CH = { sale: "bg-amber-500/20 text-amber-300", trip_chat: "bg-cyan-500/20 text-cyan-300", dispute: "bg-red-500/20 text-red-300" };
const today = () => new Date().toISOString().slice(0, 10);

export default function AutopilotDigest() {
  const [day, setDay] = useState(today());
  const [days, setDays] = useState([]);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (refresh = false) => {
    setBusy(true);
    try {
      const [{ data }, { data: ds }] = await Promise.all([api.get(`/admin/digest?date=${day}&refresh=${refresh}`), api.get("/admin/digest/days")]);
      setD(data); setDays(ds);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [day]); // eslint-disable-line

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6" data-testid="digest-tab">
      <aside className="space-y-3">
        <Input type="date" value={day} max={today()} onChange={(e) => setDay(e.target.value)} data-testid="digest-date" className="bg-slate-900 border-slate-800" />
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={busy} data-testid="digest-refresh" className="w-full bg-transparent border-slate-700">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}Rebuild digest
        </Button>
        <div className="space-y-1" data-testid="digest-days">
          {days.map((x) => (
            <button key={x.day} onClick={() => setDay(x.day)} className={`w-full flex justify-between px-3 py-2 rounded-lg text-sm border ${x.day === day ? "border-cyan-500/50 bg-[#161b28]" : "border-slate-800 bg-[#131722] hover:border-slate-600"}`}>
              <span className="font-mono">{x.day}</span><span className="text-slate-400">{x.count} msgs</span>
            </button>
          ))}
        </div>
      </aside>
      <section className="space-y-4">
        {!d ? <p className="text-slate-500">Loading…</p> : (
          <>
            <div className="p-5 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-500/5 border border-cyan-500/30" data-testid="digest-summary">
              <div className="flex items-center gap-2 mb-3"><Bot className="w-5 h-5 text-cyan-300" /><p className="font-display font-bold">What your AI said on {d.day}</p>
                <span className="ml-auto text-xs font-mono text-slate-400">{d.count} messages</span></div>
              <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{d.summary}</p>
              <div className="flex gap-2 mt-3">{Object.entries(d.by_channel || {}).map(([k, v]) => <Badge key={k} className={`${CH[k]} border-0 text-[10px] uppercase`}>{k.replace("_", " ")} · {v}</Badge>)}</div>
            </div>
            <div className="space-y-2" data-testid="digest-items">
              {d.items.map((it, i) => (
                <div key={i} className="p-4 rounded-xl bg-[#131722] border border-slate-800">
                  <div className="flex items-center gap-2 text-xs mb-1.5">
                    <Badge className={`${CH[it.channel]} border-0 text-[10px] uppercase`}>{it.channel.replace("_", " ")}</Badge>
                    <span className="text-slate-300 font-semibold truncate">{it.context}</span>
                    {it.with && <span className="text-slate-500">· with {it.with}</span>}
                    <span className="ml-auto font-mono text-slate-500">{new Date(it.at).toLocaleTimeString()}</span>
                    <Link to={it.link} className="text-cyan-400 hover:text-cyan-300"><ExternalLink className="w-3.5 h-3.5" /></Link>
                  </div>
                  <p className="text-sm text-slate-200 whitespace-pre-wrap">{it.text}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
